import React, { useMemo, useState, useLayoutEffect, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FaMapMarkerAlt, FaSlash, FaPalette } from 'react-icons/fa';
import ColorPicker from '../common/ColorPicker';
import PlaceWidget from './PlaceWidget';
import SearchBar from './saved_places/SearchBar';
import TypeFilter from './saved_places/TypeFilter';
import { usePlaceContext } from './PlaceContext';
import { useDragAndDrop } from './drag_and_drop/useDragAndDrop';
import '../../styles/components/itinerary/SavedPlaces.css';
import useApi from '../../Api';
import { debounce } from 'lodash';
import { useAuth } from '../../AuthContext';

const SavedPlaces = React.memo(({ selectedItinerary = null, readOnly = false }) => {
  const { stateRef, state, removePlaceFromSaved, savePlace, saveItineraryColumns, setColumnVisibility, setColumnMarkerColor } = usePlaceContext();
  const { savedPlaces } = state;
  const savedPlacesRef = useRef(null);
  const colorPickerRef = useRef(null);
  const colorPickerIconRef = useRef(null);
  const { token } = useAuth();
  const [columns, setColumns] = useState([]);
  const [columnsToSave, setColumnsToSave] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [isVisible, setIsVisible] = useState(true);
  const [colorPickerVisible, setColorPickerVisible] = useState(false);
  const [color, setColor] = useState(selectedItinerary ? selectedItinerary.columnMarkerColors?.['saved-places'] || '#FFFFFF' : '#FFFFFF');
  const apiClient = useApi();

  useLayoutEffect(() => {
    if (selectedItinerary) {
      if (selectedItinerary.columns) {
        setColumns(selectedItinerary.columns);
      }
      setIsVisible(selectedItinerary.columnVisibility?.['saved-places'] ?? true);
    }
  }, [selectedItinerary]);

  useEffect(() => {
    if (selectedItinerary) {
      setIsVisible(selectedItinerary.columnVisibility?.['saved-places'] ?? true);
      setColor(selectedItinerary.columnMarkerColors?.['saved-places'] || '#FFFFFF');
    }
  }, [selectedItinerary]);

  useEffect(() => {
    if (columnsToSave) {
      saveItineraryColumns(columnsToSave);
      setColumnsToSave(null);
    }
  }, [columnsToSave, saveItineraryColumns]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target) && !colorPickerIconRef.current.contains(event.target)) {
        setColorPickerVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const updateMarkersVisibilityAPI = useCallback(debounce(async (newVisibility) => {
    const itineraryId = stateRef.current.selectedItinerary.id;
    try {
      const endpoint = newVisibility ? `/saved_places/${itineraryId}/show` : `/saved_places/${itineraryId}/hide`;
      await apiClient.patch(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error updating Saved Places visibility:', error);
    }
  }, 200), [apiClient, token, stateRef]);

  const toggleMarkers = useCallback(() => {
    const newVisibility = !isVisible;
    setIsVisible(newVisibility);
    setColumnVisibility('saved-places', newVisibility);
    if (!readOnly) {
      updateMarkersVisibilityAPI(newVisibility);
    }
  }, [isVisible, setColumnVisibility, readOnly, updateMarkersVisibilityAPI]);

  const toggleColorPicker = () => {
    setColorPickerVisible(prev => !prev);
  };

  const handleChangeComplete = useCallback(debounce(async (color) => {
    setColor(color.hex);
    setColumnMarkerColor('saved-places', color.hex);
    if (!readOnly) {
      const itineraryId = stateRef.current.selectedItinerary.id;
      try {
        await apiClient.patch(`/saved_places/${itineraryId}/color`, {
          color: color.hex,
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Error updating Saved Places color:', error);
      }
    }
    setColorPickerVisible(false);
  }, 200), [stateRef, apiClient, setColumnMarkerColor, token, readOnly]);

  const { handleDrop, handleDragOver, handleDragLeave, setDragData } = useDragAndDrop(setColumns, setColumnsToSave, savePlace, savedPlaces, selectedItinerary, savedPlacesRef);

  const filteredPlaces = useMemo(() => {
    if (!selectedItinerary) return [];
    const itineraryPlaces = savedPlaces[selectedItinerary.id] || [];
    return itineraryPlaces.filter((place) => {
      const placeName = (place?.name || '').toLowerCase();
      const placeTypes = place?.types || [];
      const meetsSearch = searchTerm === '' || placeName.includes(searchTerm.toLowerCase());
      const meetsType = typeFilter === '' || placeTypes.some((type) => (type || '').toLowerCase() === typeFilter.toLowerCase());
      return meetsSearch && meetsType;
    });
  }, [savedPlaces, selectedItinerary, searchTerm, typeFilter]);

  const renderedPlaces = useMemo(() => {
    if (!selectedItinerary) return null;
    return filteredPlaces.map((place, index) => (
      <div key={place.id} data-index={index} draggable={!readOnly}>
        <PlaceWidget
          place={place}
          columnId="saved-places"
          placeIndex={index}
          onRemove={() => !readOnly && removePlaceFromSaved(place)}
          setDragData={setDragData}
          readOnly={readOnly}
        />
      </div>
    ));
  }, [filteredPlaces, selectedItinerary, removePlaceFromSaved, setDragData, readOnly]);

  useLayoutEffect(() => {
    if (colorPickerVisible && colorPickerIconRef.current && colorPickerRef.current) {
      const rect = colorPickerIconRef.current.getBoundingClientRect();
      colorPickerRef.current.style.top = `${rect.bottom + window.scrollY}px`;
      colorPickerRef.current.style.left = `${rect.left + window.scrollX}px`;
    }
  }, [colorPickerVisible]);

  return (
    <>
      <div className="saved-places-header">
        <h3 className="saved-places-title">Saved Places</h3>
        <div className="saved-places-action-icons">
          <button onClick={toggleMarkers} className="saved-places-icon-button" aria-label={isVisible ? "Hide Markers" : "Show Markers"}>
            <div className="saved-places-icon-container">
              <FaMapMarkerAlt className="saved-places-icon-marker" />
              {!isVisible && <FaSlash className="saved-places-icon-slash" />}
            </div>
          </button>
          <button onClick={toggleColorPicker} className="saved-places-icon-button" aria-label="Change Marker Color" ref={colorPickerIconRef}>
            <FaPalette />
          </button>
        </div>
        {colorPickerVisible && (
          <div className="saved-places-color-picker-popover" ref={colorPickerRef}>
            <ColorPicker color={color} onChangeComplete={handleChangeComplete} />
          </div>
        )}
        <div className="filters-container">
          <SearchBar savedPlaces={savedPlaces} selectedItinerary={selectedItinerary} onSearchTermChange={setSearchTerm} />
          <TypeFilter savedPlaces={savedPlaces} selectedItinerary={selectedItinerary} onTypeFilterChange={setTypeFilter} />
        </div>
      </div>
      <div className="saved-places-container" ref={savedPlacesRef} {...(!readOnly && { onDrop: (e) => handleDrop(e, 'saved-places'), onDragOver: (e) => handleDragOver(e, 'saved-places'), onDragLeave: (e) => handleDragLeave(e, 'saved-places') })}>
        <div className="saved-places-box">
          {renderedPlaces && renderedPlaces.length > 0 ? renderedPlaces : (<div className="empty-saved-places">Saved places will show up here</div>)}
        </div>
      </div>
    </>
  );
});

SavedPlaces.propTypes = {
  selectedItinerary: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    columns: PropTypes.array,
    columnVisibility: PropTypes.object
  }),
  readOnly: PropTypes.bool
};

export default SavedPlaces;
